import type { PassportSchemaDef } from "../types.js";

/**
 * EARLY-POSITIONING DRAFT — the ESPR working plan 2025-2030 lists tyres in the
 * ~2027 delegated-act wave. Modeled on the EU Tyre Labelling Regulation
 * (EU) 2020/740 (rolling-resistance, wet-grip and external-noise classes,
 * snow/ice grip), UNECE R30/R54 type approval, and REACH restrictions on
 * highly-aromatic extender oils (PAH). Turkish relevance is high (Petlas,
 * Brisa/Bridgestone, Goodyear İzmit).
 *
 * Field ids are STABLE; trued up when the delegated act lands by adding fields
 * / flipping `required`/`tier`, never by renaming ids.
 */
export const tyreV1: PassportSchemaDef = {
  id: "tyre-v1",
  version: "1.0.0-draft",
  title: {
    tr: "Lastik Ürün Pasaportu",
    en: "Tyre Product Passport",
    de: "Reifen-Produktpass",
  },
  jsonLdContext: {
    "@version": 1.1,
    "@vocab": "https://vocab.idpp.net/tyre/v1#",
    schema: "https://schema.org/",
    gs1: "https://gs1.org/voc/",
    identifier: "gs1:gtin",
    issuer: "schema:issuedBy",
    issuedAt: "schema:dateCreated",
  },
  categories: [
    {
      id: "identity",
      labels: { tr: "Ürün Kimliği", en: "Product Identity", de: "Produktidentität" },
      fields: [
        {
          id: "identity.productName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Ürün adı", en: "Product name", de: "Produktname" },
        },
        {
          id: "identity.productDescription",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Ürün özeti", en: "Product summary", de: "Produktbeschreibung" },
          description: "Short description of the product and its intended use.",
          presentation: { kind: "summary" },
        },
        {
          id: "identity.gtin",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "GTIN", en: "GTIN", de: "GTIN" },
          description: "GS1 Global Trade Item Number (8/12/13/14 digits).",
        },
        {
          id: "identity.tyreClass",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["c1-passenger", "c2-light-truck", "c3-heavy"],
          labels: { tr: "Lastik sınıfı", en: "Tyre class", de: "Reifenklasse" },
          description: "EU tyre-label class: C1 (passenger), C2 (light truck), C3 (heavy).",
        },
        {
          id: "identity.tyreSize",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Ebat", en: "Tyre size", de: "Reifengröße" },
          description: "E.g. 205/55 R16.",
          highlight: true,
        },
        {
          id: "identity.loadIndex",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Yük endeksi", en: "Load index", de: "Lastindex" },
        },
        {
          id: "identity.speedRating",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Hız kategorisi", en: "Speed rating", de: "Geschwindigkeitsindex" },
        },
        {
          id: "identity.brand",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Marka", en: "Brand", de: "Marke" },
        },
        {
          id: "identity.model",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Model/desen", en: "Model/pattern", de: "Modell/Profil" },
        },
        {
          id: "identity.manufacturerName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Üretici adı", en: "Manufacturer name", de: "Herstellername" },
        },
        {
          id: "identity.countryOfManufacture",
          type: "country",
          required: true,
          tier: "public",
          labels: { tr: "Üretim ülkesi", en: "Country of manufacture", de: "Herstellungsland" },
          description: "ISO 3166-1 alpha-2 code (e.g. TR).",
        },
        {
          id: "identity.dotCode",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "DOT/parti kodu", en: "DOT/batch code", de: "DOT-/Chargencode" },
        },
        {
          id: "identity.commodityCode",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Emtia kodu (GTİP/CN)", en: "Commodity code (CN/HS)", de: "Warennummer (KN/HS)" },
          description: "Needed for the EU Registry registration record.",
        },
      ],
    },
    {
      id: "labelling",
      labels: { tr: "AB Lastik Etiketi", en: "EU Tyre Label", de: "EU-Reifenlabel" },
      fields: [
        {
          id: "labelling.rollingResistanceClass",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["a", "b", "c", "d", "e"],
          labels: {
            tr: "Yuvarlanma direnci sınıfı",
            en: "Rolling resistance class",
            de: "Rollwiderstandsklasse",
          },
          description: "Fuel-efficiency class per (EU) 2020/740.",
          highlight: true,
        },
        {
          id: "labelling.wetGripClass",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["a", "b", "c", "d", "e"],
          labels: { tr: "Islak zemin tutuş sınıfı", en: "Wet grip class", de: "Nasshaftungsklasse" },
          highlight: true,
        },
        {
          id: "labelling.externalRollingNoiseDb",
          type: "number",
          required: false,
          tier: "public",
          unit: "dB",
          labels: {
            tr: "Dış yuvarlanma gürültüsü",
            en: "External rolling noise",
            de: "Externes Rollgeräusch",
          },
          highlight: true,
        },
        {
          id: "labelling.noiseClass",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["a", "b", "c"],
          labels: { tr: "Gürültü sınıfı", en: "Noise class", de: "Geräuschklasse" },
        },
        {
          id: "labelling.snowGrip",
          type: "boolean",
          required: false,
          tier: "public",
          labels: { tr: "Kar tutuşu (3PMSF)", en: "Snow grip (3PMSF)", de: "Schneegriffigkeit (3PMSF)" },
          highlight: true,
        },
        {
          id: "labelling.iceGrip",
          type: "boolean",
          required: false,
          tier: "public",
          labels: { tr: "Buz tutuşu", en: "Ice grip", de: "Eisgriffigkeit" },
        },
      ],
    },
    {
      id: "materials",
      labels: { tr: "Malzemeler", en: "Materials", de: "Materialien" },
      fields: [
        {
          id: "materials.rubberComposition",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Kauçuk bileşimi", en: "Rubber composition", de: "Gummizusammensetzung" },
          description: "Main compound summary.",
        },
        {
          id: "materials.naturalRubberPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: { tr: "Doğal kauçuk oranı", en: "Natural rubber content", de: "Naturkautschukanteil" },
        },
        {
          id: "materials.syntheticRubberPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Sentetik kauçuk oranı",
            en: "Synthetic rubber content",
            de: "Synthesekautschukanteil",
          },
        },
        {
          id: "materials.recycledContentPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: { tr: "Geri dönüştürülmüş içerik", en: "Recycled content", de: "Rezyklatanteil" },
        },
        {
          id: "materials.silicaUsed",
          type: "boolean",
          required: false,
          tier: "public",
          labels: { tr: "Silika kullanıldı", en: "Silica used", de: "Silica verwendet" },
        },
        {
          id: "materials.highlyAromaticOilsFree",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "Yüksek aromatik yağ içermez (PAH)",
            en: "Free of highly-aromatic oils (PAH)",
            de: "Frei von hocharomatischen Ölen (PAK)",
          },
          description: "REACH restricts PAH-rich extender oils in tyres.",
        },
        {
          id: "materials.svhcPresent",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "SVHC içerir (>%0,1 a/a)",
            en: "Contains SVHC (>0.1% w/w)",
            de: "Enthält SVHC (>0,1 % w/w)",
          },
        },
      ],
    },
    {
      id: "performance",
      labels: { tr: "Performans", en: "Performance", de: "Leistung" },
      fields: [
        {
          id: "performance.standardConformity",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Standart uygunluğu", en: "Standard conformity", de: "Normkonformität" },
          description: "Type-approval standard, e.g. UNECE R30 / R54.",
        },
        {
          id: "performance.mileageKm",
          type: "number",
          required: false,
          tier: "public",
          unit: "km",
          labels: { tr: "Tahmini kilometre ömrü", en: "Estimated mileage", de: "Geschätzte Laufleistung" },
        },
        {
          id: "performance.loadCapacityKg",
          type: "number",
          required: false,
          tier: "public",
          unit: "kg",
          labels: { tr: "Yük kapasitesi", en: "Load capacity", de: "Tragfähigkeit" },
        },
        {
          id: "performance.maxSpeedKmh",
          type: "number",
          required: false,
          tier: "public",
          unit: "km/h",
          labels: { tr: "Azami hız", en: "Maximum speed", de: "Höchstgeschwindigkeit" },
        },
        {
          id: "performance.retreadable",
          type: "boolean",
          required: false,
          tier: "public",
          labels: { tr: "Sırt kaplamaya uygun", en: "Retreadable", de: "Runderneuerbar" },
        },
      ],
    },
    {
      id: "circularity",
      labels: { tr: "Döngüsellik", en: "Circularity", de: "Kreislauffähigkeit" },
      fields: [
        {
          id: "circularity.recyclable",
          type: "boolean",
          required: true,
          tier: "public",
          labels: { tr: "Geri dönüştürülebilir", en: "Recyclable", de: "Recycelbar" },
          highlight: true,
        },
        {
          id: "circularity.endOfLifeRoute",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["retreading", "material-recycling", "energy-recovery", "other"],
          labels: { tr: "Ömür sonu rotası", en: "End-of-life route", de: "Verwertungsweg" },
        },
        {
          id: "circularity.retreadingPotential",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "Sırt kaplama potansiyeli",
            en: "Retreading potential",
            de: "Runderneuerungspotenzial",
          },
        },
        {
          id: "circularity.collectionInfo",
          type: "string",
          required: true,
          tier: "public",
          labels: {
            tr: "Ömür sonu toplama bilgisi",
            en: "End-of-life collection information",
            de: "Informationen zur Altreifensammlung",
          },
        },
        {
          id: "circularity.takeBackProgramUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: { tr: "Geri alma programı", en: "Take-back program", de: "Rücknahmeprogramm" },
        },
      ],
    },
  ],
};
